import { useRef, type CSSProperties, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { deploymentOptions } from '../data';
import type { ModelId } from '../types';

interface ModelTabsProps {
  model: ModelId;
  onSelect: (model: ModelId) => void;
}

const ModelTabs = ({ model, onSelect }: ModelTabsProps) => {
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const lastIndex = deploymentOptions.length - 1;
    let target: number;

    switch (event.key) {
      case 'ArrowRight':
        target = index === lastIndex ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        target = index === 0 ? lastIndex : index - 1;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    onSelect(deploymentOptions[target].id);
    tabs.current[target]?.focus();
  };

  return (
    <div className="mt-2 mb-[22px] grid gap-2.5 min-[601px]:grid-cols-3" role="tablist" aria-label="Deployment model">
      {deploymentOptions.map((option, index) => (
        <Button
          key={option.id}
          ref={element => { tabs.current[index] = element; }}
          type="button"
          variant="outline"
          role="tab"
          id={`deployment-tab-${option.id}`}
          aria-controls="deployment-content"
          aria-selected={model === option.id}
          tabIndex={model === option.id ? 0 : -1}
          style={{ '--model-accent': option.accent, '--model-tint': option.tint } as CSSProperties}
          className={cn(
            'block h-auto cursor-pointer rounded-[18px] border-2 px-5 py-[18px] text-left whitespace-normal shadow-none transition-colors motion-reduce:transition-none',
            'hover:border-muted-foreground hover:text-foreground focus-visible:ring-(--model-accent)',
            model === option.id
              ? 'border-(--model-accent) bg-(--model-tint) hover:bg-(--model-tint)'
              : 'border-border bg-card hover:bg-card',
          )}
          onClick={() => onSelect(option.id)}
          onKeyDown={event => handleKeyDown(event, index)}
        >
          <span className={cn('block text-[21px] font-bold tracking-tight', model === option.id && 'text-(--model-accent)')}>
            <span className="mr-2 inline-block size-2.5 rounded-full bg-(--model-accent) align-[2px]" aria-hidden="true" />
            {option.label}
          </span>
          <span className="mt-0.5 block text-[14.5px] leading-relaxed font-normal text-muted-foreground">{option.description}</span>
        </Button>
      ))}
    </div>
  );
};

export default ModelTabs;
